import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Check } from "lucide-react";
import { api } from "../lib/api";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import FixMatchDialog from "../components/FixMatchDialog";
import { CONDITION_LABELS } from "../lib/conditions";
import { useCurrency } from "../context/CurrencyContext";

const CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"];

function SetNameEditor({ card, onSaved }) {
  const [value, setValue] = useState(card.set_name || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const skipFetchRef = useRef(false);

  useEffect(() => setValue(card.set_name || ""), [card.set_name]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }
    const q = value.trim();
    if (q.length < 2 || q === (card.set_name || "")) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get("/sets/suggest", { params: { q } });
        const list = res.data.suggestions || [];
        setSuggestions(list);
        setOpen(list.length > 0);
      } catch {
        setOpen(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [value, card.set_name]);

  const persist = async (v) => {
    if (v === (card.set_name || "")) return;
    try {
      const res = await api.patch(`/cards/${card.id}`, { set_name: v });
      onSaved(res.data);
      toast.success(v ? `Filed under ${v}` : "Set name cleared");
    } catch {
      toast.error("Couldn't update the set name");
    }
  };

  const pick = (s) => {
    skipFetchRef.current = true;
    setValue(s);
    setSuggestions([]);
    setOpen(false);
    persist(s);
  };

  return (
    <div className="relative inline-block max-w-full">
      <input
        data-testid={`review-set-input-${card.id}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => { setOpen(false); persist(value.trim()); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
          if (e.key === "Escape") setOpen(false);
        }}
        placeholder="Set unknown — type it here"
        title="The set / collection this card is from — type it if the AI couldn't find it"
        style={{ width: `${Math.min(Math.max((value || "").length + 3, 14), 30)}ch` }}
        className="bg-transparent text-xs text-zinc-400 border border-transparent hover:border-white/15 focus:border-[#A78BFA]/50 rounded px-1 py-0.5 max-w-full focus:outline-none placeholder:text-zinc-600"
      />
      {open && (
        <div
          data-testid={`review-set-suggestions-${card.id}`}
          className="absolute left-0 top-full mt-1 z-20 min-w-[12rem] max-w-[16rem] bg-[#1a1a1e] border border-white/10 rounded-lg shadow-xl overflow-hidden text-left"
        >
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`review-set-suggestion-${card.id}-${s.replace(/\s+/g, "-").toLowerCase()}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className="block w-full text-left px-3 py-1.5 text-xs text-zinc-300 hover:bg-[#7C3AED]/20 hover:text-white transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Review() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { format } = useCurrency();
  const [job, setJob] = useState(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const wasProcessingRef = useRef(false);

  useEffect(() => {
    let timer;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get(`/videos/${jobId}`);
        if (cancelled) return;
        setJob((prev) => {
          if (!prev) return res.data;
          // Keep local edits (condition/status/match changes) for cards already shown.
          const edited = new Map(prev.cards.map((c) => [c.id, c]));
          return { ...res.data, cards: res.data.cards.map((c) => edited.get(c.id) || c) };
        });
        if (res.data.status === "processing" || res.data.status === "uploaded") {
          wasProcessingRef.current = true;
          timer = setTimeout(load, 2500);
        } else if (res.data.status === "completed" && wasProcessingRef.current) {
          wasProcessingRef.current = false;
          toast.success("Scan complete — all cards are in!");
        }
      } catch {
        if (!cancelled) toast.error("Could not load results");
      }
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [jobId]);

  const processing = job?.status === "processing" || job?.status === "uploaded";

  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const cancelScan = async () => {
    if (!confirmCancel) {
      setConfirmCancel(true);
      setTimeout(() => setConfirmCancel(false), 4000);
      return;
    }
    setCancelling(true);
    try {
      await api.post(`/videos/${jobId}/cancel`);
      setJob((j) => ({ ...j, status: "cancelled" }));
      toast.success("Scan cancelled — cards already priced are kept");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't cancel the scan");
    } finally {
      setCancelling(false);
      setConfirmCancel(false);
    }
  };

  const pendingCards = job?.cards?.filter((c) => c.status !== "confirmed") || [];

  const confirmAll = async () => {
    setConfirmingAll(true);
    try {
      await api.post("/cards/confirm", { card_ids: pendingCards.map((c) => c.id) });
      setJob((j) => ({ ...j, cards: j.cards.map((c) => ({ ...c, status: "confirmed" })) }));
      toast.success(`${pendingCards.length} card${pendingCards.length === 1 ? "" : "s"} confirmed`);
    } catch {
      toast.error("Could not confirm all cards. Please try again.");
    } finally {
      setConfirmingAll(false);
    }
  };

  const confirmCard = async (cardId) => {
    await api.patch(`/cards/${cardId}`, { status: "confirmed" });
    setJob((j) => ({ ...j, cards: j.cards.map((c) => (c.id === cardId ? { ...c, status: "confirmed" } : c)) }));
  };

  const removeCard = async (cardId) => {
    await api.delete(`/cards/${cardId}`);
    setJob((j) => ({ ...j, cards: j.cards.filter((c) => c.id !== cardId) }));
  };

  const replaceCard = (updated) => {
    setJob((j) => ({ ...j, cards: j.cards.map((c) => (c.id === updated.id ? updated : c)) }));
  };

  const toggleDisplayImage = async (card) => {
    const next = card.display_image === "match" ? "scan" : "match";
    try {
      const res = await api.patch(`/cards/${card.id}`, { display_image: next });
      replaceCard(res.data);
      toast.success(next === "match" ? "Web image selected as the card photo" : "Your scan will be the card photo");
    } catch {
      toast.error("Couldn't update the image choice");
    }
  };

  const setCondition = async (cardId, condition) => {
    try {
      const res = await api.patch(`/cards/${cardId}`, { condition });
      replaceCard(res.data);
    } catch {
      toast.error("Couldn't update condition. Please try again.");
    }
  };

  if (!job) {
    return <div className="min-h-screen bg-[#05060f] flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-[#A78BFA] rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#05060f] text-white px-4 sm:px-6 py-10 sm:py-16">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8 sm:mb-10 flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold mb-1">Review your scan</h1>
            <p className="text-zinc-400 text-sm">
              {processing
                ? "Cards appear below as they're identified and priced — you can start reviewing right away."
                : `${job.cards?.length || 0} cards detected. Compare each snapshot to the AI match, then confirm or discard.`}
            </p>
            {processing && (
              <div data-testid="review-live-progress" className="mt-3 flex items-center gap-3">
                <Loader2 className="w-4 h-4 animate-spin text-[#A78BFA] shrink-0" />
                <div className="space-y-1">
                  <p className="text-xs text-zinc-400">
                    {job.cards_processed || 0} of {job.total_cards_detected || "?"} cards priced
                  </p>
                  {job.total_cards_detected > 0 && (
                    <Progress
                      value={Math.round(((job.cards_processed || 0) / job.total_cards_detected) * 100)}
                      className="bg-white/10 h-1.5 w-48"
                    />
                  )}
                </div>
                <button
                  data-testid="review-cancel-scan-button"
                  onClick={cancelScan}
                  disabled={cancelling}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors disabled:opacity-50 ${confirmCancel ? "border-red-500 text-red-400 bg-red-500/10" : "border-red-500/30 text-red-400/80 hover:text-red-400 hover:bg-red-500/10"}`}
                >
                  {cancelling ? "Stopping..." : confirmCancel ? "Really stop?" : "Cancel scan"}
                </button>
              </div>
            )}
            {job.status === "failed" && (
              <p data-testid="review-failed-note" className="mt-2 text-xs text-red-400">
                {job.error_message || "Processing failed. Please re-scan."}
                {job.cards?.length ? " The cards below were processed before it stopped." : ""}
              </p>
            )}
            {job.status === "cancelled" && (
              <p data-testid="review-cancelled-note" className="mt-2 text-xs text-[#F59E0B]">
                {job.cards?.length
                  ? `Scan cancelled — the ${job.cards.length} card${job.cards.length === 1 ? "" : "s"} that finished before it stopped ${job.cards.length === 1 ? "is" : "are"} kept below.`
                  : "Scan cancelled — no cards had finished processing."}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {pendingCards.length > 0 && (
              <Button
                data-testid="review-confirm-all-button"
                onClick={confirmAll}
                disabled={confirmingAll}
                variant="outline"
                className="border-[#A78BFA]/40 text-[#A78BFA] hover:bg-[#A78BFA]/10 rounded-full px-6 disabled:opacity-50"
              >
                {confirmingAll ? "Confirming..." : `Confirm all (${pendingCards.length})`}
              </Button>
            )}
            <Link to="/binder">
              <Button data-testid="review-go-to-binder-button" className="bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full px-6">
                Go to Binder
              </Button>
            </Link>
          </div>
        </div>

        <div className="space-y-4">
          {job.cards?.map((card) => (
            <div key={card.id} data-testid={`review-card-row-${card.id}`} className="flex flex-col gap-4 md:grid md:grid-cols-[auto_auto_1fr_auto] md:items-center border border-white/10 bg-[#111113] rounded-xl p-4">
              <div className="flex justify-center gap-6 md:contents">
                <div className="flex flex-col items-center gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Your Snapshot</span>
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${card.captured_image_url}`}
                    alt="captured"
                    className="w-24 h-32 object-cover rounded-lg border border-white/10"
                    data-testid={`review-captured-image-${card.id}`}
                  />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">AI Match (Web)</span>
                  {card.reference_image_url ? (
                    <>
                      <button
                        type="button"
                        data-testid={`review-use-match-${card.id}`}
                        onClick={() => toggleDisplayImage(card)}
                        title={card.display_image === "match" ? "Selected as the card photo — click to switch back to your scan" : "Click to use this web image as the card photo"}
                        className={`relative rounded-lg transition-shadow ${card.display_image === "match" ? "ring-2 ring-emerald-400 shadow-[0_0_14px_rgba(52,211,153,0.35)]" : "ring-1 ring-white/10 hover:ring-[#A78BFA]/60"}`}
                      >
                        <img src={pcImageUrl(card.reference_image_url, "240")} onError={pcImageFallback} alt="reference" className="w-24 h-32 object-cover rounded-lg" data-testid={`review-reference-image-${card.id}`} />
                        {card.display_image === "match" && (
                          <span data-testid={`review-match-selected-${card.id}`} className="absolute -top-1.5 -right-1.5 bg-emerald-400 text-black rounded-full p-0.5">
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </span>
                        )}
                      </button>
                      <span data-testid={`review-photo-choice-${card.id}`} className={`text-[9px] ${card.display_image === "match" ? "text-emerald-400" : "text-zinc-600"}`}>
                        {card.display_image === "match" ? "Card photo: this image" : "Card photo: your scan — tap to switch"}
                      </span>
                    </>
                  ) : (
                    <div className="w-24 h-32 flex items-center justify-center text-xs text-zinc-600 border border-white/10 rounded-lg">No match</div>
                  )}
                  {card.match_verified && (
                    <span data-testid={`review-verified-badge-${card.id}`} className="flex items-center gap-1 text-[10px] text-emerald-400">
                      <ShieldCheck className="w-3 h-3" /> Verified
                    </span>
                  )}
                  {!card.match_verified && card.reference_image_url && (
                    <span data-testid={`review-probable-badge-${card.id}`} className="flex items-center gap-1 text-[10px] text-[#F59E0B]">
                      Probable match
                    </span>
                  )}
                  <FixMatchDialog
                    card={card}
                    onUpdated={replaceCard}
                    trigger="Fix match"
                    triggerTestId={`review-fix-match-${card.id}`}
                  />
                </div>
              </div>
              <div className="text-center md:text-left [&>div]:justify-center md:[&>div]:justify-start">
                <h3 className="font-display font-semibold text-white" data-testid={`review-card-name-${card.id}`}>{card.name}</h3>
                <div className="text-xs text-zinc-500 flex items-center gap-1 flex-wrap -ml-1">
                  <SetNameEditor card={card} onSaved={replaceCard} />
                  {card.card_number ? <span>· #{card.card_number}</span> : null}
                  {card.variant && card.variant.toLowerCase() !== (card.rarity || "").toLowerCase() ? <span>· {card.variant}</span> : null}
                </div>
                <p className="text-xs text-zinc-500 capitalize">{card.game_type} · {card.rarity || "rarity unknown"}</p>
                {card.id_confidence === "low" && (
                  <p data-testid={`review-low-confidence-${card.id}`} className="text-[11px] text-[#F59E0B] mt-1">Low AI confidence — double-check this match</p>
                )}
                {(card.quantity || 1) > 1 && (
                  <p data-testid={`review-stacked-note-${card.id}`} className="text-[11px] text-emerald-400 mt-1">
                    Duplicate detected — stacked onto your existing copy (you now own ×{card.quantity})
                  </p>
                )}
                <p className={`font-mono-plex font-semibold mt-1 ${["estimate_similar", "ai_estimate"].includes(card.price_source) ? "text-[#F59E0B]" : "text-[#A78BFA]"}`} data-testid={`review-card-price-${card.id}`}>
                  {card.current_price != null ? `${["estimate_similar", "ai_estimate"].includes(card.price_source) ? "~" : ""}${format(card.current_price)}` : "—"}{" "}
                  <span className="text-zinc-500 text-xs">
                    ({card.price_source === "sold_comps" ? "last-sold eBay median" : card.price_source === "market_data_scrape" ? "live market avg" : card.price_source === "estimate_similar" ? "estimated from similar cards" : card.price_source === "ai_estimate" ? "AI estimate" : card.price_source === "rate_limited" ? "market source busy — retry soon" : "no price data"})
                  </span>
                </p>
                {!card.is_graded && (
                  <div data-testid={`review-condition-row-${card.id}`} className="flex items-center gap-1 mt-2 flex-wrap">
                    {CONDITIONS.map((c) => (
                      <button
                        key={c}
                        data-testid={`review-condition-${c}-${card.id}`}
                        onClick={() => setCondition(card.id, c)}
                        title={CONDITION_LABELS[c]}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
                          (card.condition || "NM") === c
                            ? "bg-[#A78BFA] text-[#05060f] border-[#A78BFA]"
                            : "border-white/15 text-zinc-400 hover:border-[#A78BFA]/50 hover:text-white"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}
                {!card.match_verified && card.matched_listings?.length > 0 && card.reference_image_source !== "user_uploaded" && (
                  <Link
                    to={`/card/${card.id}`}
                    data-testid={`review-choose-match-${card.id}`}
                    className="inline-flex items-center gap-1 text-[11px] text-[#F59E0B] hover:text-white mt-1 underline underline-offset-2"
                  >
                    Wrong or no match? Pick from {card.matched_listings.length} option{card.matched_listings.length === 1 ? "" : "s"}
                  </Link>
                )}
              </div>
              <div className="flex md:flex-col gap-2">
                <Button data-testid={`review-confirm-button-${card.id}`} size="sm" onClick={() => confirmCard(card.id)} disabled={card.status === "confirmed"} className="flex-1 md:flex-none bg-[#A78BFA] text-[#05060f] hover:bg-[#8B5CF6] rounded-full">
                  {card.status === "confirmed" ? "Confirmed" : "Confirm"}
                </Button>
                <Button data-testid={`review-discard-button-${card.id}`} size="sm" variant="outline" onClick={() => removeCard(card.id)} className="flex-1 md:flex-none border-white/20 hover:bg-white/5 rounded-full">
                  Discard
                </Button>
              </div>
            </div>
          ))}
          {job.cards?.length === 0 && (
            <p className="text-zinc-500 text-sm" data-testid="review-empty-state">No cards left to review.</p>
          )}
        </div>
      </div>
    </div>
  );
}
